import React, { useState, useCallback } from 'react';
import { Box, Button, Chip, Grid, IconButton, MenuItem, TextField, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Add, Delete, Schedule, Event, AccessTime, Settings } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDateTime } from '@campfire/hot-date';
import { useFormikContext } from 'formik';
import { TaskFormType } from '../../admin-console-volunteer-profile-model';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { AutoLocationField } from '../../../../../../../common/auto-location-field/AutoLocationField';
import { AddBookableSlotDialog } from './AddBookableSlotDialog';

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hr' },
  { value: 90, label: '1.5 hr' },
  { value: 120, label: '2 hr' },
  { value: 180, label: '3 hr' },
  { value: 240, label: '4 hr' },
];

const TIMEZONE_OPTIONS = [
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Asia/Singapore',
  'UTC',
];

const DAYS = [
  { code: 'MO', label: 'Mon' },
  { code: 'TU', label: 'Tue' },
  { code: 'WE', label: 'Wed' },
  { code: 'TH', label: 'Thu' },
  { code: 'FR', label: 'Fri' },
  { code: 'SA', label: 'Sat' },
  { code: 'SU', label: 'Sun' },
];

type AvailabilityEntry = { day: string; startTime: string; endTime: string };

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      padding: '20px',
      marginBottom: '16px',
      border: '1px solid #e0e0e0',
      borderRadius: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
    },
    cardIcon: {
      fontSize: '20px',
      color: theme.palette.primary.main,
      marginRight: '10px',
    },
    cardTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    cardSubtitle: {
      fontSize: '13px',
      color: '#808080',
      marginTop: '2px',
    },
    dayRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    dayToggle: {
      width: '40px',
      height: '28px',
      borderRadius: '14px',
      border: '1px solid #dcdcdc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 600,
      marginRight: '12px',
      userSelect: 'none',
      transition: 'all 0.15s ease',
    },
    dayToggleActive: {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      borderColor: theme.palette.primary.main,
    },
    dayToggleInactive: {
      backgroundColor: '#f5f5f5',
      color: '#999',
    },
    slotItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      border: '1px solid #e8e8e8',
      borderRadius: '10px',
      marginBottom: '8px',
      backgroundColor: '#fafafa',
    },
    toggleButton: {
      border: '1px solid',
      borderColor: '#dcdcdc',
      borderRadius: '4px',
      textTransform: 'none' as const,
      padding: '7px 12px',
      fontWeight: 600,
      fontSize: '13px',
      color: '#666',
      backgroundColor: '#fff',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    toggleButtonSelected: {
      color: '#FCFCFC !important',
      backgroundColor: `${theme.palette.primary.main} !important`,
      borderColor: `${theme.palette.primary.main} !important`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.dark} !important`,
      },
    },
  })
);

type ManagerOption = {
  managerId: string;
  profile: { preferredName: string; lastName: string; avatarUrl: string | null };
};

interface EventBookingConfigSectionProps {
  disabled: boolean;
  taskId?: string;
  bookableTimeSlots?: Array<{
    bookableTimeSlotId: string;
    startDateTime: string;
    endDateTime: string | null;
    timezone: string;
    remoteLocation: string | null;
    maxCapacity: number;
    currentBookings: number;
    message: string | null;
    isVirtual: boolean;
  }>;
  availableManagers?: ManagerOption[];
  onRefetch?: () => Promise<any> | void;
}

export function EventBookingConfigSection({
  disabled,
  taskId,
  bookableTimeSlots,
  availableManagers = [],
  onRefetch,
}: EventBookingConfigSectionProps) {
  const classes = useStyles();
  useCampfireTheme();
  const { values, setFieldValue } = useFormikContext<TaskFormType>();
  const [showSlotDialog, setShowSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState<any>(null);
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const removeFetch = useCampfireFetch({ defer: true });

  const schedule: AvailabilityEntry[] = values.availabilitySchedule ?? [];
  const activeDays = new Set(schedule.map((e) => e.day));

  const manualSlots = (bookableTimeSlots ?? []).filter((s) => !s.isVirtual);

  const toggleDay = useCallback(
    (dayCode: string) => {
      if (disabled) return;
      const current = values.availabilitySchedule ?? [];
      if (current.some((e: AvailabilityEntry) => e.day === dayCode)) {
        setFieldValue(
          'availabilitySchedule',
          current.filter((e: AvailabilityEntry) => e.day !== dayCode)
        );
      } else {
        setFieldValue('availabilitySchedule', [...current, { day: dayCode, startTime: '09:00', endTime: '17:00' }]);
      }
    },
    [disabled, values.availabilitySchedule, setFieldValue]
  );

  const updateDayTime = useCallback(
    (dayCode: string, field: 'startTime' | 'endTime', value: string) => {
      const current = values.availabilitySchedule ?? [];
      setFieldValue(
        'availabilitySchedule',
        current.map((e: AvailabilityEntry) => (e.day === dayCode ? { ...e, [field]: value } : e))
      );
    },
    [values.availabilitySchedule, setFieldValue]
  );

  const handleRemoveSlot = async (slotId: string) => {
    try {
      await removeFetch.run({
        url: `${apiUrl}/vm/task/event/bookable-slot/remove`,
        method: 'post',
        data: { bookableTimeSlotId: slotId },
      });
      setSnackbar({ open: true, message: 'Time slot removed', variant: 'success' });
      if (onRefetch) onRefetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove time slot', variant: 'error' });
    }
  };

  const formatSlotDateTime = (isoString: string) => {
    const dt = unpackToDateTime(isoString);
    return dt.toFormat('ccc d MMM, h:mm a');
  };

  return (
    <Grid item xs={12} style={{ marginTop: '8px' }}>
      {/* Weekly Availability Card */}
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <Schedule className={classes.cardIcon} />
          <Box>
            <Typography className={classes.cardTitle}>Weekly Availability</Typography>
            <Typography className={classes.cardSubtitle}>Set the days and hours volunteers can book</Typography>
          </Box>
        </Box>

        <Box style={{ marginBottom: '16px' }}>
          {DAYS.map(({ code, label }) => {
            const isActive = activeDays.has(code);
            const entry = schedule.find((e) => e.day === code);

            return (
              <Box key={code} className={classes.dayRow}>
                <Box
                  className={`${classes.dayToggle} ${isActive ? classes.dayToggleActive : classes.dayToggleInactive}`}
                  onClick={() => toggleDay(code)}
                >
                  {label}
                </Box>

                {isActive && entry ? (
                  <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                    <TextField
                      type='time'
                      size='small'
                      variant='outlined'
                      value={entry.startTime}
                      onChange={(e) => updateDayTime(code, 'startTime', e.target.value)}
                      disabled={disabled}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: '13px', padding: '6px 8px' } }}
                      style={{ width: '120px' }}
                    />
                    <Typography variant='body2' color='textSecondary'>
                      to
                    </Typography>
                    <TextField
                      type='time'
                      size='small'
                      variant='outlined'
                      value={entry.endTime}
                      onChange={(e) => updateDayTime(code, 'endTime', e.target.value)}
                      disabled={disabled}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: '13px', padding: '6px 8px' } }}
                      style={{ width: '120px' }}
                    />
                  </Box>
                ) : (
                  <Typography variant='body2' style={{ color: '#bbb', fontSize: '13px' }}>
                    Unavailable
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              select
              fullWidth
              label='Slot Duration'
              variant='outlined'
              size='small'
              value={values.defaultSlotDuration ?? ''}
              onChange={(e) => setFieldValue('defaultSlotDuration', e.target.value ? Number(e.target.value) : null)}
              disabled={disabled}
            >
              <MenuItem value=''>Select duration</MenuItem>
              {DURATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Individual Time Slots Card */}
      {!taskId && (
        <Box className={classes.card} style={{ opacity: 0.6 }}>
          <Box className={classes.cardHeader}>
            <Event className={classes.cardIcon} />
            <Box>
              <Typography className={classes.cardTitle}>Individual Time Slots</Typography>
              <Typography className={classes.cardSubtitle}>Save the task first to add individual time slots</Typography>
            </Box>
          </Box>
        </Box>
      )}
      {taskId && (
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <Event className={classes.cardIcon} />
            <Box>
              <Typography className={classes.cardTitle}>Individual Time Slots</Typography>
              <Typography className={classes.cardSubtitle}>Add one-off booking slots for specific dates</Typography>
            </Box>
          </Box>

          {manualSlots.length > 0 ? (
            <Box style={{ marginBottom: '12px' }}>
              {manualSlots.map((slot) => (
                <Box key={slot.bookableTimeSlotId} className={classes.slotItem}>
                  <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                    <AccessTime style={{ fontSize: '16px', color: '#757575' }} />
                    <Typography variant='body2' style={{ fontWeight: 500 }}>
                      {formatSlotDateTime(slot.startDateTime)}
                    </Typography>
                    {slot.endDateTime && (
                      <Typography variant='body2' color='textSecondary'>
                        – {unpackToDateTime(slot.endDateTime).toFormat('h:mm a')}
                      </Typography>
                    )}
                    <Chip
                      size='small'
                      label={`${slot.currentBookings}/${slot.maxCapacity}`}
                      style={{ fontSize: '11px', height: '22px' }}
                    />
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      onClick={() => {
                        setEditingSlot(slot);
                        setShowSlotDialog(true);
                      }}
                      disabled={disabled}
                    >
                      <Settings fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveSlot(slot.bookableTimeSlotId)}
                      disabled={disabled}
                    >
                      <Delete fontSize='small' style={{ color: '#c62828' }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: '12px' }}>
              No individual time slots configured
            </Typography>
          )}

          <TabletButton
            variant='outlined'
            color='primary'
            onClick={() => {
              setEditingSlot(null);
              setShowSlotDialog(true);
            }}
            disabled={disabled}
          >
            <Add style={{ fontSize: '18px', marginRight: '4px' }} />
            Add Time Slot
          </TabletButton>
        </Box>
      )}

      {/* Defaults Card */}
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <Settings className={classes.cardIcon} />
          <Box>
            <Typography className={classes.cardTitle}>Default Settings</Typography>
            <Typography className={classes.cardSubtitle}>Applied to all auto-generated slots</Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              label='Max Capacity'
              variant='outlined'
              size='small'
              type='number'
              inputProps={{ min: 1 }}
              value={values.defaultMaxCapacity ?? 1}
              onChange={(e) => setFieldValue('defaultMaxCapacity', Math.max(1, Number(e.target.value)))}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              select
              fullWidth
              label='Timezone'
              variant='outlined'
              size='small'
              value={values.defaultTimezone ?? ''}
              onChange={(e) => setFieldValue('defaultTimezone', e.target.value || null)}
              disabled={disabled}
            >
              <MenuItem value=''>Default</MenuItem>
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {availableManagers.length > 0 && (
            <Grid item xs={12}>
              <Typography variant='body2' style={{ marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>
                Default Managers
              </Typography>
              <Box display='flex' flexWrap='wrap' style={{ gap: '6px', marginBottom: '8px' }}>
                {(values.defaultManagerIds ?? []).map((mId: string) => {
                  const mgr = availableManagers.find((m) => m.managerId === mId);
                  if (!mgr) return null;
                  return (
                    <Chip
                      key={mId}
                      label={`${mgr.profile.preferredName} ${mgr.profile.lastName}`}
                      size='small'
                      onDelete={
                        disabled
                          ? undefined
                          : () =>
                              setFieldValue(
                                'defaultManagerIds',
                                (values.defaultManagerIds ?? []).filter((id: string) => id !== mId)
                              )
                      }
                    />
                  );
                })}
              </Box>
              <TextField
                select
                fullWidth
                variant='outlined'
                size='small'
                value=''
                label='Add a manager'
                disabled={disabled}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id && !(values.defaultManagerIds ?? []).includes(id)) {
                    setFieldValue('defaultManagerIds', [...(values.defaultManagerIds ?? []), id]);
                  }
                }}
              >
                {availableManagers
                  .filter((m) => !(values.defaultManagerIds ?? []).includes(m.managerId))
                  .map((m) => (
                    <MenuItem key={m.managerId} value={m.managerId}>
                      {m.profile.preferredName} {m.profile.lastName}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant='body2' style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>
              Default Location
            </Typography>
            <Box display='flex' width='100%' mb={1}>
              <Button
                fullWidth
                className={`${classes.toggleButton} ${
                  !values.defaultRemoteLocation ? classes.toggleButtonSelected : ''
                }`}
                onClick={() => {
                  setFieldValue('defaultRemoteLocation', null);
                }}
                disabled={disabled}
                style={{ marginRight: '8px' }}
              >
                Specific Location
              </Button>
              <Button
                fullWidth
                className={`${classes.toggleButton} ${
                  values.defaultRemoteLocation != null ? classes.toggleButtonSelected : ''
                }`}
                onClick={() => {
                  setFieldValue('defaultRemoteLocation', '');
                }}
                disabled={disabled}
              >
                Remote / Virtual
              </Button>
            </Box>
          </Grid>
          {values.defaultRemoteLocation != null ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Remote Location Details'
                variant='outlined'
                size='small'
                multiline
                rows={2}
                value={values.defaultRemoteLocation}
                onChange={(e) => setFieldValue('defaultRemoteLocation', e.target.value || '')}
                disabled={disabled}
                placeholder='Meeting link, phone number, or instructions'
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <AutoLocationField name='defaultPlacesAddress' label='Location' fullWidth variant='outlined' helper />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Default Message'
              variant='outlined'
              size='small'
              multiline
              rows={2}
              value={values.defaultMessage ?? ''}
              onChange={(e) => setFieldValue('defaultMessage', e.target.value || null)}
              disabled={disabled}
              placeholder='Message shown to volunteers when booking'
            />
          </Grid>
        </Grid>
      </Box>

      {showSlotDialog && taskId && (
        <AddBookableSlotDialog
          open
          onClose={() => {
            setShowSlotDialog(false);
            setEditingSlot(null);
          }}
          onSaved={async () => {
            if (onRefetch) await onRefetch();
          }}
          taskId={taskId}
          availableManagers={availableManagers}
          editSlot={editingSlot}
        />
      )}
    </Grid>
  );
}
